import React, { useEffect } from "react";
import { X, Briefcase, GraduationCap, Award, Clock, Wallet, Quote } from "lucide-react";
import { COUNTRY_DETAILS } from "@/lib/countryDetails";
import { useApp } from "@/store/AppContext";

const ROUTE_ICON = { Work: Briefcase, Study: GraduationCap, PR: Award };
const ROUTE_COLOR = {
  Work:  "bg-[#C9A961]/15 text-[#8B6F30] border-[#C9A961]/40",
  Study: "bg-sky-100 text-sky-800 border-sky-200",
  PR:    "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function CountryDeepDive({ country, count, onClose }) {
  const { openContact } = useApp();
  const d = COUNTRY_DETAILS[country];

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!d) return null;

  return (
    <div
      data-testid="country-deep-dive"
      className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:w-[560px] h-full bg-[#F7F6F2] overflow-y-auto shadow-2xl animate-[slideIn_.4s_ease]"
      >
        <div className={`relative h-44 bg-gradient-to-br ${d.gradient} text-white px-6 pt-6`}>
          <button
            data-testid="country-deep-dive-close"
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="text-[11px] uppercase tracking-[0.3em] opacity-80">Destination deep-dive</div>
          <h2 className="font-serif text-4xl mt-2">{country}</h2>
          <div className="text-xs opacity-80 mt-1">{d.hero}</div>
          <div className="absolute right-6 bottom-4 bg-black/35 backdrop-blur rounded-full px-3 py-1 text-[11px] uppercase tracking-wider">
            {count || 0} approvals mapped
          </div>
        </div>

        <section className="px-6 py-6">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 mb-3">Visa routes we handle</h3>
          <div className="space-y-3">
            {d.routes.map((r) => {
              const Icon = ROUTE_ICON[r.kind] || Briefcase;
              return (
                <div
                  key={r.kind}
                  data-testid={`route-${r.kind.toLowerCase()}`}
                  className="bg-white rounded-2xl border border-slate-100 p-4 flex items-start gap-3 hover:border-[#C9A961] transition-colors"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${ROUTE_COLOR[r.kind]}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{r.kind}</span>
                    </div>
                    <div className="text-sm font-semibold text-slate-800 mt-0.5">{r.name}</div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {r.time}</span>
                      <span className="inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> {r.cost}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="px-6 pb-6">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 mb-3">Recent successes</h3>
          <div className="space-y-3">
            {d.stories.map((s) => (
              <div key={s.name} className="bg-white rounded-2xl border border-slate-100 p-4">
                <Quote className="w-4 h-4 text-[#C9A961] mb-2" />
                <p className="text-sm text-slate-700 leading-relaxed italic">"{s.quote}"</p>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-2">{s.name}</div>
              </div>
            ))}
          </div>
        </section>

        <div className="px-6 pb-8">
          <button
            data-testid="country-deep-dive-cta"
            onClick={() => { onClose(); openContact({ target_country: country }); }}
            className="w-full rounded-xl bg-[#0B1E3F] hover:bg-[#162A4D] text-white px-5 py-3.5 text-sm font-semibold"
          >
            Start my {country} application →
          </button>
          <p className="text-[11px] text-slate-400 text-center mt-3">
            Senior-led practice. 950+ approvals across 46 countries.
          </p>
        </div>

        <style>{`
          @keyframes slideIn { from { transform: translateX(40px); opacity: 0.4; } to { transform: translateX(0); opacity: 1; } }
        `}</style>
      </aside>
    </div>
  );
}
